import ConfirmationDialog from '@rio-cloud/rio-uikit/ConfirmationDialog';
import Dialog from '@rio-cloud/rio-uikit/Dialog';
import Select, { SelectOption } from '@rio-cloud/rio-uikit/Select';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { usePutCompoundAccessMutation } from '../api/serviceProviderApi';
import { handleQueryError } from '../notifications/ErrorNotification';
import { showSuccessNotification } from '../notifications/SuccessNotification';
import { CompoundAccess, ServiceProvider } from './serviceProvider.types';
import { runInBackground } from '../../../configuration/setup/backgroundActions';

const useCompoundAccessLabel = (accessesLevel: CompoundAccess) => {
    const intl = useIntl();

    switch (accessesLevel) {
        case CompoundAccess.NONE:
            return intl.formatMessage({ id: 'outboundOrderBook.serviceProviders.compoundAccess.none' });
        case CompoundAccess.READ_ONLY:
            return intl.formatMessage({ id: 'outboundOrderBook.serviceProviders.compoundAccess.readOnly' });
        case CompoundAccess.READ_WRITE:
            return intl.formatMessage({ id: 'outboundOrderBook.serviceProviders.compoundAccess.readWrite' });
    }
};

export const CompoundAccessSelect = ({ serviceProvider }: { serviceProvider: ServiceProvider }) => {
    const [requestedAccessChange, setRequestedAccessChange] = useState<CompoundAccess | undefined>(undefined);
    const [putCompoundAccess, compoundAccessRequest] = usePutCompoundAccessMutation();
    const oldAccess = serviceProvider.compoundAccess;

    const options = [
        {
            id: CompoundAccess.NONE,
            label: useCompoundAccessLabel(CompoundAccess.NONE),
            icon: <span className="rioglyph rioglyph-ban-circle text-color-rating-1" />,
        },
        {
            id: CompoundAccess.READ_ONLY,
            label: useCompoundAccessLabel(CompoundAccess.READ_ONLY),
            icon: <span className="rioglyph rioglyph-eye-open text-color-rating-2" />,
        },
        {
            id: CompoundAccess.READ_WRITE,
            label: useCompoundAccessLabel(CompoundAccess.READ_WRITE),
            icon: <span className="rioglyph rioglyph-checkbox-checked text-color-rating-4" />,
        },
    ];

    const changeCompoundAccess = (option: SelectOption | undefined) => {
        const nextAccess = option?.id as CompoundAccess | undefined;
        if (nextAccess !== undefined && requestedAccessChange !== oldAccess) {
            setRequestedAccessChange(nextAccess);
        }
    };

    const confirmAccessChange = () => {
        if (requestedAccessChange === undefined) return;

        runInBackground(async () => {
            try {
                await putCompoundAccess({
                    accountId: serviceProvider.accountId,
                    compoundAccess: requestedAccessChange,
                }).unwrap();
                showSuccessNotification('outboundOrderBook.serviceProviders.compoundAccess.success');
            } catch (error: unknown) {
                handleQueryError(error);
            } finally {
                closeDialog();
            }
        });
    };

    const closeDialog = () => setRequestedAccessChange(undefined);

    return (
        <>
            <Select
                disabled={compoundAccessRequest.isLoading}
                options={options}
                value={[requestedAccessChange ?? oldAccess]}
                onChange={changeCompoundAccess}
            />
            <ConfirmationDialog
                show={requestedAccessChange !== undefined}
                title={<FormattedMessage id="outboundOrderBook.serviceProviders.compoundAccess.confirmation.title" />}
                content={
                    <FormattedMessage
                        id="outboundOrderBook.serviceProviders.compoundAccess.confirmation.text"
                        values={{
                            serviceProviderName: <strong>{serviceProvider.serviceProviderName}</strong>,
                            from: <strong>{useCompoundAccessLabel(oldAccess)}</strong>,
                            // we use oldAccess as fallback -- we usually do not show the dialog anymore,
                            // this is just relevant for the flyout effect, which is so fast that you cannot
                            // read the displayed string anyway
                            to: <strong>{useCompoundAccessLabel(requestedAccessChange ?? oldAccess)}</strong>,
                        }}
                    />
                }
                bsSize={Dialog.SIZE_SM}
                onClickConfirm={confirmAccessChange}
                onClickCancel={closeDialog}
                cancelButtonText={<FormattedMessage id="outboundOrderBook.common.button.cancel" />}
                confirmButtonText={<FormattedMessage id="outboundOrderBook.common.button.apply" />}
            />
        </>
    );
};
